/**
 * `growth/config` — PURE resolution of the ads configuration.
 *
 * Same three-layer pattern as the monetization kit
 * (`monetization/config.ts`): **code defaults → env → admin UI
 * overrides**. Env and UI share a source: the env provides the default and each override
 * overwrites ONE key live; deleting the override returns control to the env.
 *
 * **OFF by default is sacred**: without `ADS_ENABLED`/`VITE_ADS_ENABLED` the whole layer
 * is inert, `network` is `none` and no SDK is loaded.
 */

import type { AdContentRating, AdInitOptions, AdsConfig, AdTier, AdTierRegime } from "./types";
import { AD_CONTENT_RATINGS, AD_TIER_REGIMES, AD_TIERS, STRICTEST_AD_TIER_REGIME } from "./types";

/** `none` backend: no-op. It is the DEFAULT and the one that guarantees "no ads, no SDK". */
export const AD_NETWORK_NONE = "none";

/**
 * Backends known to the kit. `none` first on purpose.
 * `fake` is the deterministic provider of the e2e suites (no network or DOM): it is NEVER
 * configured in a real environment.
 */
export const AD_NETWORKS = [AD_NETWORK_NONE, "admob", "adsense", "fake"] as const;

/** Admin UI override keys (same source as the env). */
export const AD_OVERRIDE_KEYS = [
  "ads.enabled",
  "ads.network",
  "ads.tier",
  "ads.child_directed",
  "ads.max_content_rating",
  "ads.client_id",
] as const;

export type AdOverrideKey = (typeof AD_OVERRIDE_KEYS)[number];

export function isValidAdOverrideKey(key: string): key is AdOverrideKey {
  return (AD_OVERRIDE_KEYS as readonly string[]).includes(key);
}

/** Code defaults: EVERYTHING off. Enabling ads is an explicit decision. */
export function builtinAdsDefaults(): AdsConfig {
  return {
    enabled: false,
    network: AD_NETWORK_NONE,
    tier: "A",
    childDirected: false,
    maxAdContentRating: "G",
    clientId: "",
  };
}

function parseBool(v: string | undefined): boolean | undefined {
  if (v === undefined) {
    return undefined;
  }
  const s = v.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(s)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(s)) {
    return false;
  }
  return undefined;
}

function isAdTier(v: string): v is AdTier {
  return (AD_TIERS as readonly string[]).includes(v);
}

function parseTier(v: string | undefined): AdTier | undefined {
  const s = v?.trim().toUpperCase();
  return s && isAdTier(s) ? s : undefined;
}

function isAdContentRating(v: string): v is AdContentRating {
  return (AD_CONTENT_RATINGS as readonly string[]).includes(v);
}

function parseRating(v: string | undefined): AdContentRating | undefined {
  const s = v?.trim().toUpperCase();
  return s && isAdContentRating(s) ? s : undefined;
}

/**
 * The STRICTEST of two ratings. `AD_CONTENT_RATINGS` is ordered from most
 * strict to most lax, so "strictest" = lower index.
 */
export function strictestAdContentRating(a: AdContentRating, b: AdContentRating): AdContentRating {
  return AD_CONTENT_RATINGS.indexOf(a) <= AD_CONTENT_RATINGS.indexOf(b) ? a : b;
}

/**
 * Declared regime of a tier. An unknown tier falls back to the STRICTEST
 * regime (fail-closed): with no declared policy, no advertising is served.
 */
export function adTierRegime(tier: string): AdTierRegime {
  return isAdTier(tier) ? AD_TIER_REGIMES[tier] : STRICTEST_AD_TIER_REGIME;
}

/**
 * Layer 2: env. Both forms of the monorepo are accepted — `ADS_*` (server, and the
 * e2e suites) and `VITE_ADS_*` (web bundle) — with the Vite one winning on the
 * client, which is where the build rules.
 */
export function applyAdsEnv(config: AdsConfig, env: Record<string, string | undefined>): void {
  const enabled = parseBool(env.VITE_ADS_ENABLED) ?? parseBool(env.ADS_ENABLED);
  if (enabled !== undefined) {
    config.enabled = enabled;
  }
  const network = (env.VITE_ADS_NETWORK ?? env.ADS_NETWORK)?.trim().toLowerCase();
  if (network) {
    config.network = network;
  }
  const tier = parseTier(env.VITE_ADS_TIER ?? env.ADS_TIER);
  if (tier) {
    config.tier = tier;
  }
  const child = parseBool(env.VITE_ADS_CHILD_DIRECTED) ?? parseBool(env.ADS_CHILD_DIRECTED);
  if (child !== undefined) {
    config.childDirected = child;
  }
  const rating = parseRating(env.VITE_ADS_MAX_CONTENT_RATING ?? env.ADS_MAX_CONTENT_RATING);
  if (rating) {
    config.maxAdContentRating = rating;
  }
  const clientId = (env.VITE_ADS_CLIENT_ID ?? env.ADS_CLIENT_ID)?.trim();
  if (clientId) {
    config.clientId = clientId;
  }
}

/** Layer 3: a back-office override (same semantics as monetization). */
export function applyAdsOverride(config: AdsConfig, key: string, value: unknown): void {
  switch (key) {
    case "ads.enabled": {
      if (typeof value === "boolean") {
        config.enabled = value;
      }
      return;
    }
    case "ads.network": {
      if (typeof value === "string" && value.trim()) {
        config.network = value.trim().toLowerCase();
      }
      return;
    }
    case "ads.tier": {
      const tier = parseTier(typeof value === "string" ? value : undefined);
      if (tier) {
        config.tier = tier;
      }
      return;
    }
    case "ads.child_directed": {
      if (typeof value === "boolean") {
        config.childDirected = value;
      }
      return;
    }
    case "ads.max_content_rating": {
      const rating = parseRating(typeof value === "string" ? value : undefined);
      if (rating) {
        config.maxAdContentRating = rating;
      }
      return;
    }
    case "ads.client_id": {
      if (typeof value === "string") {
        config.clientId = value.trim();
      }
      return;
    }
    default:
  }
}

/**
 * **Layer 4 — the regime clamp. Classification beats the flag.**
 *
 * Lowers the resolved configuration to the declared FLOOR of its tier
 * (`AD_TIER_REGIMES`). It only hardens: neither an env, nor a back-office
 * override, nor an oversight can relax a tier's regime below
 * its minimum. It is a property of the KIT, not of each app — if tomorrow a clone
 * declares a tier, it inherits its regime without writing a line.
 *
 * What it guarantees:
 *   - tiers without ads (`C`, `D`): `enabled=false` and `network=none` ⇒ zero SDK;
 *   - child tiers (`K`, and the fail-closed floor of an unknown tier):
 *     `childDirected=true` ALWAYS;
 *   - rating ceiling per tier, and **`G` whenever there is a child regime**,
 *     wherever it comes from (from the tier or from an env that HARDENS a tier `A`).
 *
 * Idempotent and monotonic: applying it twice gives the same, and it never raises.
 *
 * History: the C/D clamp existed from day one, but the child-tier one
 * did not — `VITE_ADS_CHILD_DIRECTED=false` was enough to serve behavioral
 * ads to a minor. Melodimals (the only tier K app) discovered it and today it is
 * the same implementation as the canon (`listto/packages/shared/src/growth`).
 */
export function clampAdsConfigToTier(config: AdsConfig): void {
  const regime = adTierRegime(config.tier);
  if (!regime.adsAllowed) {
    config.enabled = false;
    config.network = AD_NETWORK_NONE;
  }
  if (regime.childDirected) {
    config.childDirected = true;
  }
  // The tier ceiling and the child-regime one are combined: the lower one rules.
  const ceiling = config.childDirected
    ? strictestAdContentRating(regime.maxAdContentRating, "G")
    : regime.maxAdContentRating;
  config.maxAdContentRating = strictestAdContentRating(config.maxAdContentRating, ceiling);
}

/** Full resolution: defaults → env → overrides → regime clamp. */
export function resolveAdsConfig(
  env: Record<string, string | undefined>,
  overrides: readonly { key: string; value: unknown }[] = [],
): AdsConfig {
  const config = builtinAdsDefaults();
  applyAdsEnv(config, env);
  for (const o of overrides) {
    applyAdsOverride(config, o.key, o.value);
  }
  clampAdsConfigToTier(config);
  return config;
}

/**
 * Init options HARDENED against the resolved config.
 *
 * The SECOND hole of the same family: `init(opts)` took
 * `opts.childDirected` as-is, so a caller could undo the regime
 * the clamp had just guaranteed — the door was closed and the window was left
 * open. Here the caller can only **harden** (request child-directed,
 * or a lower rating); relaxing is impossible.
 */
export function hardenAdInitOptions(config: AdsConfig, opts: AdInitOptions = {}): AdInitOptions {
  const childDirected = config.childDirected || opts.childDirected === true;
  const requested = opts.maxAdContentRating ?? config.maxAdContentRating;
  let rating = strictestAdContentRating(config.maxAdContentRating, requested);
  if (childDirected) {
    rating = strictestAdContentRating(rating, "G");
  }
  return {
    childDirected,
    maxAdContentRating: rating,
    // It does not affect the policy: passes as-is.
    ...(opts.testDeviceIds ? { testDeviceIds: opts.testDeviceIds } : {}),
  };
}

/**
 * Should this app load the ads kit? Combines the ads config with the verdict
 * of the monetization kit (`shouldShowAds(config, tier)` rules: premium ⇒ 0 ads
 * immediately). Without `enabled` or with network `none`, **zero SDK**.
 */
export function adsAreLive(config: AdsConfig, monetizationSaysAds: boolean): boolean {
  return config.enabled && config.network !== AD_NETWORK_NONE && monetizationSaysAds;
}
